/**
 * Phase 20.3 — RGM Shuffle Page (/shuffle, /rgm-shuffle).
 *
 * Mixed discovery feed of reposted content. Filters: all / tracks / beats /
 * following / trending / new. Cards keep playback through the global
 * PlayerProvider so reposted music survives navigation.
 */
import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import SharedNavigation from "@/components/SharedNavigation";
import ShuffleCard from "@/components/ShuffleCard";
import ShuffleRadioHero from "@/components/ShuffleRadioHero";
import api from "@/lib/api";
import { useCurrentUser } from "@/lib/useCurrentUser";
import { toast } from "sonner";

const FILTERS = [
  { key: "all",       label: "All",       icon: "🔀" },
  { key: "tracks",    label: "Tracks",    icon: "🎤" },
  { key: "beats",     label: "Beats",     icon: "🎹" },
  { key: "following", label: "Following", icon: "👥", auth: true },
  { key: "trending",  label: "Trending",  icon: "🔥" },
  { key: "new",       label: "New",       icon: "✨" },
];

const ShufflePage = ({ mode = "shuffle" }) => {
  // Phase 20.6 follow-up — when mounted at `/reposts` we render the repost
  // activity feed only (no Radio Hero) so RGM Reposts stays a distinct
  // destination from RGM Shuffle Radio. Same data, different surface.
  const isReposts = mode === "reposts";
  const { currentUser } = useCurrentUser();
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ tracks: 0, beats: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback((f) => {
    setLoading(true); setError(null);
    api.get(`/shuffle?filter=${f}&limit=30`)
      .then((r) => {
        setItems(r.data?.items || []);
        setCounts(r.data?.counts || { tracks: 0, beats: 0 });
      })
      .catch((err) => {
        if (err?.response?.status === 401 && f === "following") {
          setError("Sign in to see what people you follow are reposting.");
          setItems([]);
        } else {
          setError(err?.response?.data?.detail || "Could not load RGM Shuffle");
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(filter); }, [filter, load]);

  return (
    <div className="min-h-screen bg-[#08080c]" data-testid={isReposts ? "rgm-reposts-page" : "rgm-shuffle-page"}>
      <SharedNavigation currentUser={currentUser} />
      <div className="pt-24 pb-24 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <header className="mb-6">
          <p className="text-[11px] uppercase tracking-widest text-purple-300 font-bold">RGM</p>
          <h1 className="text-3xl sm:text-4xl font-black text-white">
            {isReposts ? "🔁 RGM Reposts" : "🔀 RGM Shuffle"}
          </h1>
          <p className="text-sm text-gray-400 mt-1 max-w-2xl">
            {isReposts
              ? "The repost activity feed — every track and beat your community is co-signing. Hit any card to play it through the global mini-player."
              : "What the community is co-signing right now. Tracks and beats reposted by artists, producers, and supporters — all in one mixed feed."}
            {counts.tracks + counts.beats > 0 && (
              <span className="text-purple-300 ml-2">
                · {counts.tracks} tracks · {counts.beats} beats in this view
              </span>
            )}
          </p>
        </header>

        {/* Phase 20.6 — Pandora-style Start Shuffle Radio (hidden on /reposts) */}
        {!isReposts && <ShuffleRadioHero currentUser={currentUser} />}

        {/* Filter bar */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2" data-testid="shuffle-filter-bar">
          {FILTERS.filter((f) => !f.auth || currentUser).map((f) => (
            <button
              key={f.key}
              onClick={() => {
                if (f.auth && !currentUser) {
                  toast.error("Sign in to use this filter");
                  return;
                }
                setFilter(f.key);
              }}
              data-testid={`shuffle-filter-${f.key}`}
              className={`px-3 py-1.5 rounded-full text-sm font-bold border whitespace-nowrap transition-colors ${
                filter === f.key
                  ? "bg-purple-600/40 border-purple-400 text-white"
                  : "bg-black/30 border-gray-700 text-gray-300 hover:border-purple-500/60"
              }`}
            >
              <span className="mr-1">{f.icon}</span>{f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 text-gray-400" data-testid="shuffle-loading">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-t-2 border-purple-500" />
            <p className="mt-3 text-sm uppercase tracking-widest">Shuffling…</p>
          </div>
        ) : error ? (
          <Card className="p-10 text-center bg-amber-500/5 border-amber-500/30"
                data-testid="shuffle-error">
            <div className="text-4xl mb-2">🔒</div>
            <p className="text-white">{error}</p>
          </Card>
        ) : items.length === 0 ? (
          <Card className="p-10 text-center bg-gray-900/40 border-gray-800"
                data-testid="shuffle-empty">
            <div className="text-5xl mb-3">🔀</div>
            <p className="text-lg font-semibold text-white">
              No reposts yet in this view.
            </p>
            <p className="text-sm text-gray-400 mt-2">
              {filter === "following"
                ? "Follow some artists and producers to start filling this feed."
                : "When artists or supporters repost tracks and beats, they'll appear here."}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4"
               data-testid="shuffle-grid">
            {items.map((item, idx) => (
              <ShuffleCard
                key={`${item.kind}_${item.repost_id || (item.kind === "track" ? item.track?.submission_id : item.beat?.beat_id)}_${idx}`}
                item={item}
                currentUserId={currentUser?.user_id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShufflePage;
